import type { RecentActivityItem } from './api';
import { formatShortDate } from 'src/lib/utils/formatDate';
import { snakeToTitleCase } from 'src/lib/utils/formatLabel';

const RESOURCE_TYPE_LABELS: Record<string, string> = {
  chat: 'Chat',
  review: 'Review',
  writing: 'Document',
  document: 'Document',
  dashboard: 'Dashboard',
  specialist: 'Specialist',
  action: 'Action',
  action_extraction: 'Uploaded document'
};

const ACTION_CHANGE_VERBS: Record<string, string> = {
  resolved: 'resolved',
  unresolved: 'reopened',
  archived: 'archived',
  unarchived: 'unarchived'
};

const APPROVAL_VERBS: Record<string, string> = {
  requested: 'requested minutes approval for',
  cancelled: 'cancelled minutes approval for',
  approved: 'approved minutes for',
  rejected: 'rejected minutes for'
};

function quote(name: string): string {
  return `'${name}'`;
}

function actorName(actor: string | null | undefined): string {
  return actor?.trim() || 'Someone';
}

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

const formatScheduledDate = (value: string | null | undefined) => formatShortDate(value, { empty: '' });

function formatClock(value: string | null | undefined): string {
  if (!value) return '';
  const [hours, minutes] = value.split(':').map((part) => Number.parseInt(part, 10));
  if (Number.isNaN(hours)) return '';
  const date = new Date(2000, 0, 1, hours, Number.isNaN(minutes) ? 0 : minutes);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function weeklyDays(csv: string | null | undefined): string {
  if (!csv) return '';
  const names = csv
    .split(',')
    .map((part) => {
      const day = WEEKDAYS[Number.parseInt(part.trim(), 10)];
      return day ? `${day}s` : '';
    })
    .filter(Boolean);
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function asMetadata(facts: RecentActivityItem['facts']): Record<string, unknown> {
  return facts.metadata ?? {};
}

function metaString(meta: Record<string, unknown>, key: string): string | undefined {
  const value = meta[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function metaNumber(meta: Record<string, unknown>, key: string): number | undefined {
  const value = meta[key];
  return typeof value === 'number' ? value : undefined;
}

function metaStringList(meta: Record<string, unknown>, key: string): string[] {
  const value = meta[key];
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}

function formatRecurrenceSubtitle(meta: Record<string, unknown>): string[] {
  const clock = formatClock(metaString(meta, 'scheduled_time'));
  const type = metaString(meta, 'repeat_type');
  let detail = '';
  if (type === 'weekly' || (!type && metaString(meta, 'repeat_days_of_week'))) {
    const days = weeklyDays(metaString(meta, 'repeat_days_of_week'));
    if (days && clock) detail = `${days} at ${clock}`;
    else if (days) detail = days;
    else if (clock) detail = `Weekly at ${clock}`;
  } else if (type === 'daily') {
    detail = clock ? `Daily at ${clock}` : 'Daily';
  } else if (type === 'monthly') {
    detail = clock ? `Monthly at ${clock}` : 'Monthly';
  } else if (type === 'yearly') {
    detail = clock ? `Yearly at ${clock}` : 'Yearly';
  } else if (clock) {
    detail = clock;
  }
  return detail ? ['Regular meeting', detail] : ['Regular meeting'];
}

function formatMeetingScheduledSubtitle(meta: Record<string, unknown>): string[] {
  if (meta.recurring === true || metaString(meta, 'repeat_type')) {
    const recurrence = formatRecurrenceSubtitle(meta);
    const when = formatScheduledDate(metaString(meta, 'scheduled_date'));
    return when ? [...recurrence, when] : recurrence;
  }
  const when = formatScheduledDate(metaString(meta, 'scheduled_date'));
  const clock = formatClock(metaString(meta, 'scheduled_time'));
  return ['One-off meeting', when, clock].filter(Boolean);
}

function formatActionAssignmentSubtitle(meta: Record<string, unknown>): string[] {
  const names = metaStringList(meta, 'assignees');
  const assignees = names.length ? names.join(', ') : 'Unassigned';
  const dueDate = formatScheduledDate(metaString(meta, 'due_date'));
  const due = dueDate ? `Due ${dueDate}` : 'No due date';
  const priorityRaw = metaString(meta, 'priority');
  const priority =
    priorityRaw === 'high' || priorityRaw === 'medium' || priorityRaw === 'low'
      ? `${priorityRaw.charAt(0).toUpperCase()}${priorityRaw.slice(1)} priority`
      : 'No priority';
  return [assignees, due, priority];
}

function formatFinalisedSubtitle(meta: Record<string, unknown>): string[] {
  const parts: string[] = [];
  const when = formatScheduledDate(metaString(meta, 'scheduled_date'));
  if (when) parts.push(when);
  const actionCount = metaNumber(meta, 'action_count');
  if (actionCount != null) {
    parts.push(`${actionCount} action${actionCount === 1 ? '' : 's'} extracted`);
  }
  const attendees = metaNumber(meta, 'attendee_count');
  if (attendees != null) {
    parts.push(`${attendees} attendee${attendees === 1 ? '' : 's'}`);
  }
  return parts;
}

export function formatActivityCopy(item: RecentActivityItem): { title: string; subtitleParts: string[] } {
  const facts = item.facts;
  const meta = asMetadata(facts);
  const actor = actorName(facts.actor);
  const subject = facts.subject || 'Untitled';

  switch (item.kind) {
    case 'group_created':
      return { title: `${actor} created ${quote(subject)}`, subtitleParts: ['New group'] };
    case 'member_added': {
      const names = metaStringList(meta, 'subjects').length ? metaStringList(meta, 'subjects') : [subject];
      const memberCount = metaNumber(meta, 'member_count') ?? names.length;
      if (memberCount > 1) {
        return {
          title: `${memberCount} members were added`,
          subtitleParts: names
        };
      }
      return {
        title: facts.actor ? `${actor} added ${quote(subject)} to the group` : `${quote(subject)} joined the group`,
        subtitleParts: [`Role: ${metaString(meta, 'role') ? snakeToTitleCase(metaString(meta, 'role')!) : 'Member'}`]
      };
    }
    case 'action_created':
      return { title: `${actor} created ${quote(subject)}`, subtitleParts: formatActionAssignmentSubtitle(meta) };
    case 'action_state_changed':
      return {
        title: `${actor} ${ACTION_CHANGE_VERBS[metaString(meta, 'action_change') ?? ''] ?? 'updated'} ${quote(subject)}`,
        subtitleParts: formatActionAssignmentSubtitle(meta)
      };
    case 'action_progress_update':
      return {
        title: `${actor} updated ${quote(subject)}`,
        subtitleParts: formatActionAssignmentSubtitle(meta)
      };
    case 'meeting_added':
      return {
        title: `${actor} scheduled ${quote(subject)}`,
        subtitleParts: formatMeetingScheduledSubtitle(meta)
      };
    case 'meeting_series_added':
      return {
        title: `${actor} scheduled ${quote(subject)}`,
        subtitleParts: formatRecurrenceSubtitle(meta)
      };
    case 'meeting_resource_added':
      return { title: `${actor} added a resource to ${quote(subject)}`, subtitleParts: ['Meeting resource'] };
    case 'meeting_approval':
      return {
        title: `${actor} ${APPROVAL_VERBS[metaString(meta, 'approval') ?? ''] ?? 'updated approval for'} ${quote(subject)}`,
        subtitleParts: ['Minutes approval']
      };
    case 'meeting_finalised':
      return {
        title: `${actor} finalised ${quote(subject)}`,
        subtitleParts: formatFinalisedSubtitle(meta)
      };
    case 'resource_shared': {
      const relation = metaString(meta, 'relation');
      const verb = relation === 'owned' ? 'uploaded' : relation === 'linked' ? 'linked' : 'shared';
      const documentTitle = metaString(meta, 'document_title');
      const typeLabel =
        metaString(meta, 'resource_type') === 'document'
          ? null
          : (RESOURCE_TYPE_LABELS[metaString(meta, 'resource_type') ?? ''] ?? 'Resource');
      return {
        title: `${actor} ${verb} ${quote(subject)}`,
        subtitleParts: [documentTitle, typeLabel].filter((part): part is string => Boolean(part))
      };
    }
    case 'dashboard_data_entered': {
      const year = metaNumber(meta, 'period_year');
      const month = metaNumber(meta, 'period_month');
      const period =
        year != null && month != null
          ? new Date(year, month - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
          : null;
      const names = metaStringList(meta, 'subjects');
      const cards = names.length ? names : [subject];
      if (cards.length > 1) {
        return {
          title: `${actor} submitted data to multiple cards in the dashboard`,
          subtitleParts: ['Dashboards', ...cards]
        };
      }
      return {
        title: `${actor} submitted data to ${quote(cards[0] || subject)}`,
        subtitleParts: ['Dashboards', period, meta.is_baseline === true ? 'Baseline' : null].filter(
          (part): part is string => Boolean(part)
        )
      };
    }
    case 'appointment_created': {
      const role = metaString(meta, 'role_name');
      const sites = metaStringList(meta, 'sites');
      const siteLabel = sites.length ? sites.join(', ') : 'All sites';
      const from = formatScheduledDate(metaString(meta, 'appointed_from'));
      const until = formatScheduledDate(metaString(meta, 'valid_until'));
      return {
        title: role ? `${actor} appointed ${quote(subject)} as ${role}` : `${actor} appointed ${quote(subject)}`,
        subtitleParts: [
          siteLabel,
          from ? `Appointed from ${from}` : null,
          until ? `Valid until ${until}` : 'No end date'
        ].filter((part): part is string => Boolean(part))
      };
    }
    case 'appointment_revoked':
      return { title: `${actor} revoked ${quote(subject)}`, subtitleParts: ['Appointment revoked'] };
    case 'certificate_added':
      return { title: `${actor} updated ${quote(subject)}`, subtitleParts: ['Certificate'] };
    case 'extraction_meeting':
    case 'extraction_report': {
      const count = metaNumber(meta, 'action_count');
      const extracted =
        count != null && count > 0
          ? `extracted ${count} action${count === 1 ? '' : 's'} from`
          : 'extracted actions from';
      return { title: `${actor} ${extracted} ${quote(subject)}`, subtitleParts: [] };
    }
    default:
      return { title: `${actor} updated ${quote(subject)}`, subtitleParts: [] };
  }
}
